/**
 * Line Follower for FlowRide Frontend
 * Handles GPS position tracking and route following functionality.
 * Projects GPS coordinates onto route lines and provides navigation guidance.
 */

import { bearing, lineString, nearestPointOnLine, point } from "@turf/turf";
import type maplibregl from "maplibre-gl";

// Note: 'distance' was removed from imports as it was not used
import type GeoJSON from "geojson";
import { type DrawnItineraryGeometry } from "../../store/ephemeral";

/** GPS position interface */
export interface GpsPosition {

    /** Latitude coordinate */
    "latitude": number;

    /** Longitude coordinate */
    "longitude": number;

    /** GPS accuracy in meters */
    "accuracy": number;

    /** Compass heading in degrees */
    "heading": number | null;

    /** Speed in meters per second */
    "speed": number | null;

    /** Timestamp when position was captured */
    "timestamp": number;
}

/** Route following state interface */
export interface FollowingState {

    /** Raw GPS position [longitude, latitude] */
    "rawPosition": [number, number];

    /** Position projected onto the route line */
    "matchedPosition": [number, number];

    /** Direction to follow in degrees */
    "heading": number;

    /** Route line direction in degrees */
    "lineDirection": number;

    /** Current speed in m/s */
    "speed": number;

    /** Whether user is currently following a line */
    "onLine": boolean;

    /** Identifier of the followed line */
    "lineId": string | null;

    /** Confidence in tracking (0-1) */
    "confidence": number;

    /** Time since last update */
    "lastUpdateTime": number;

    /** Reversed direction (true if going opposite to line direction) */
    "isReversed": boolean;

    /** Timestamp of last direction change */
    "lastDirectionChangeTime": number;
}

interface LineMatch {
    "feature":     GeoJSON.Feature;
    "matchResult": GeoJSON.Feature<GeoJSON.Point>;
    "confidence":  number;
}

interface DetectedLine {
    "feature":  GeoJSON.Feature;
    "distance": number;
}

interface CurrentLine {
    "id":             string;
    "geometry":       GeoJSON.LineString;
    "detectedAt":     number;
    "lastMatchIndex": number;
}

interface HistoryEntry {
    "position":  [number, number];
    "timestamp": number;
    "lineId":    string | null;
}

interface CachedLine {
    "feature":          GeoJSON.Feature;
    "lastDetectedTime": number;
}

/**
 * LineFollower - Service qui détecte et suit automatiquement les lignes présentes sur la carte
 */
export class LineFollower {

    private readonly map: maplibregl.Map;

    private readonly state: FollowingState;

    // Configuration
    private readonly detectionRadius: number = 50; // mètres

    private readonly maxOffLineDistance: number = 30; // mètres

    private readonly minLineConfidence: number = 0.6; // confiance minimale pour considérer une ligne

    private readonly stickToLineThreshold: number = 15; // mètres - distance pour "coller" à la ligne

    // Callbacks
    private stateChangeCallbacks: Array<( state: FollowingState ) => void> = [];

    // Cache des lignes détectées
    private readonly detectedLines = new Map<string, CachedLine>();

    // Historique des positions pour une meilleure continuité
    private positionHistory: HistoryEntry[] = [];

    // Référence à la ligne actuellement suivie
    private currentLine: CurrentLine | null = null;

    // Géométrie de l'itinéraire actif (pour le mode de suivi générique)
    private drawnItineraryGeometry: DrawnItineraryGeometry | null = null;

    /**
     * Définit l'itinéraire actif à suivre
     * @param geometry Géométrie de l'itinéraire actif
     */
    public setDrawnItineraryGeometry( geometry: DrawnItineraryGeometry | null ): void {
        // console.log( "LineFollower: setDrawnItineraryGeometry appelé avec", geometry );

        // Réinitialiser complètement le suivi quand on change d'itinéraire ou quand on le supprime
        if ( this.drawnItineraryGeometry?.id !== geometry?.id || geometry === null ) {
            // console.log( "LineFollower: Réinitialisation complète du suivi car la géométrie a changé" );
            this.currentLine = null;
            this.detectedLines.clear();
            this.positionHistory = [];

            // Réinitialiser l'état
            this.state.onLine = false;
            this.state.lineId = null;
            this.state.confidence = 0;
        }

        this.drawnItineraryGeometry = geometry;
    }

    /**
     * Initialise le LineFollower
     */
    constructor( map: maplibregl.Map, options?: {
        "detectionRadius"?:      number;
        "maxOffLineDistance"?:   number;
        "stickToLineThreshold"?: number;
    } ) {
        this.map = map;

        // Appliquer les options
        if ( options != null ) {
            this.detectionRadius = options.detectionRadius ?? this.detectionRadius;
            this.maxOffLineDistance = options.maxOffLineDistance ?? this.maxOffLineDistance;
            this.stickToLineThreshold = options.stickToLineThreshold ?? this.stickToLineThreshold;
        }

        // Initialiser l'état
        this.state = {
            "rawPosition":             [ 0, 0 ],
            "matchedPosition":         [ 0, 0 ],
            "heading":                 0,
            "lineDirection":           0,
            "speed":                   0,
            "onLine":                  false,
            "lineId":                  null,
            "confidence":              0,
            "lastUpdateTime":          Date.now(),
            "isReversed":              false,
            "lastDirectionChangeTime": 0,
        };
    }

    /**
     * Met à jour avec une nouvelle position GPS
     */
    public updatePosition( position: GpsPosition ): FollowingState {
        const now = Date.now();

        // Validation robuste des coordonnées
        if ( typeof position.latitude !== "number" || typeof position.longitude !== "number" ||
          isNaN( position.latitude ) || isNaN( position.longitude ) ) {
            console.warn( "[LineFollower] Invalid coordinates:", {
                "latitude":  position.latitude,
                "longitude": position.longitude,
            } );

            return { ...this.state };
        }

        const lonLat: [number, number] = [ position.longitude, position.latitude ];

        // Mettre à jour la position brute
        this.state.rawPosition = lonLat;
        this.state.lastUpdateTime = now;

        // Utiliser la vitesse GPS si disponible
        if ( position.speed !== null && position.speed >= 0 ) {
            this.state.speed = position.speed;
        }

        // 1. Détecter les lignes à proximité
        const nearbyLines = this.detectNearbyLines( lonLat, position.accuracy );

        // 2. Trouver la meilleure ligne à suivre
        const bestLine = this.findBestLineToFollow( lonLat, nearbyLines, position.heading );

        // 3. Si on a trouvé une ligne, la suivre
        if ( bestLine !== null ) {
            const { feature, matchResult, confidence } = bestLine;

            // Obtenir l'index du segment (propriété ajoutée par turf nearestPointOnLine)
            const props = matchResult.properties as { "index"?: number } | null;
            const index = props?.index ?? 0;

            // Mettre à jour la ligne courante
            this.updateCurrentLine( feature, index );

            // Mettre à jour l'état
            this.state.matchedPosition = matchResult.geometry.coordinates as [number, number];
            this.state.onLine = true;
            this.state.lineId = this.getFeatureId( feature );
            this.state.confidence = confidence;

            // Calculer la direction de la ligne
            const lineDirection = this.calculateLineDirection( feature, index );
            this.state.lineDirection = lineDirection;

            // Déterminer si on va dans le sens inverse de la ligne
            let isReversed = false;
            if ( position.heading !== null && position.speed !== null && position.speed > 1.0 ) {
                // Calculer la différence d'angle entre le cap GPS et la direction de la ligne
                const angleDiff = Math.abs( position.heading - lineDirection );
                const normalizedDiff = Math.min( angleDiff, 360 - angleDiff );

                // Si l'angle est supérieur à 90°, on considère qu'on va dans le sens inverse
                isReversed = normalizedDiff > 90;

                // Si on a changé de direction, mettre à jour le timestamp
                if ( isReversed !== this.state.isReversed ) {
                    this.state.lastDirectionChangeTime = Date.now();
                }
            }
            this.state.isReversed = isReversed;

            /*
             * TOUJOURS utiliser la direction de la ligne pour le cap, jamais le GPS ou la boussole
             * Si on va dans le sens inverse, inverser la direction (ajouter 180°)
             */
            if ( isReversed ) {
                this.state.heading = ( lineDirection + 180 ) % 360;
            } else {
                this.state.heading = lineDirection;
            }

            // Si on a changé de direction il y a moins de 5 secondes, conserver la direction précédente
            const timeSinceDirectionChange = now - this.state.lastDirectionChangeTime;
            if ( timeSinceDirectionChange < 5000 && this.state.lastDirectionChangeTime > 0 ) {
                // Ne pas mettre à jour le cap, garder celui qu'on avait avant
            }

            // Ajouter à l'historique
            this.addToHistory( this.state.matchedPosition, now, this.state.lineId );
        } else {
            // Pas de ligne trouvée ou trop loin de toute ligne
            this.state.matchedPosition = lonLat;
            this.state.onLine = false;
            this.state.lineId = null;
            this.state.confidence = 0;
            this.state.lineDirection = 0;

            // Si on a changé de direction il y a moins de 5 secondes, conserver la direction précédente
            const timeSinceDirectionChange = now - this.state.lastDirectionChangeTime;
            if ( timeSinceDirectionChange < 5000 && this.state.lastDirectionChangeTime > 0 ) {
                // Ne pas mettre à jour le cap, garder celui qu'on avait avant
            } else if ( this.currentLine !== null ) {
                // Si on a encore une ligne en cache, utiliser sa direction
                const lastKnownIndex = this.currentLine.lastMatchIndex;
                const lineGeometry = this.currentLine.geometry;
                if ( lastKnownIndex < lineGeometry.coordinates.length - 1 ) {
                    const start = lineGeometry.coordinates[lastKnownIndex];
                    const end = lineGeometry.coordinates[lastKnownIndex + 1];
                    const directionFromLine = bearing( point( start ), point( end ) );

                    // Appliquer la direction de la ligne, en tenant compte du sens de déplacement
                    this.state.heading = this.state.isReversed ? ( directionFromLine + 180 ) % 360 : directionFromLine;
                }
            } else {
                // En dernier recours, utiliser le cap GPS si disponible
                if ( position.heading !== null ) {
                    this.state.heading = position.heading;
                }
            }

            // Si on perd une ligne, la conserver en cache un moment
            if ( this.currentLine !== null ) {
                // Garder la ligne courante pendant max 10 secondes
                if ( now - this.currentLine.detectedAt > 10000 ) {
                    this.currentLine = null;
                }
            }

            // Ajouter à l'historique
            this.addToHistory( lonLat, now, null );
        }

        // Notifier les observateurs
        this.notifyStateChange();

        return { ...this.state };
    }

    /**
     * Détecte les lignes à proximité de la position
     */
    private detectNearbyLines(
        position: [number, number],
        accuracy: number,
    ): DetectedLine[] {
        const results: DetectedLine[] = [];

        // Utiliser un rayon adapté à la précision GPS
        const radius = Math.max( this.detectionRadius, accuracy * 1.5 ) * 10;

        try {
            // Vérifier si nous avons un itinéraire actif
            if ( this.drawnItineraryGeometry === null || this.drawnItineraryGeometry.polylines.length === 0 ) {
                console.warn( "LineFollower: Aucune géométrie d'itinéraire active disponible" );

                return [];
            }

            // Créer des lignes à partir des polylines de l'itinéraire actif
            for ( const polyline of this.drawnItineraryGeometry.polylines ) {
                if ( polyline.coordinates.length < 2 ) {
                    continue;
                }

                // Vérifier que les coordonnées sont bien formatées (tableaux de 2 nombres)
                const validCoordinates = polyline.coordinates.filter( ( coord ) => Array.isArray( coord ) &&
                  coord.length === 2 &&
                  typeof coord[0] === "number" &&
                  typeof coord[1] === "number" &&
                  !isNaN( coord[0] ) &&
                  !isNaN( coord[1] ) );

                if ( validCoordinates.length < 2 ) {
                    continue;
                }

                // Filtrer les points identiques consécutifs qui causent des problèmes avec Turf
                const uniqueCoordinates = validCoordinates.filter( ( coord, index, array ) => {
                    if ( index === 0 ) {
                        return true;
                    }

                    // Comparer avec le point précédent
                    const prev = array[index - 1];

                    return !( coord[0] === prev[0] && coord[1] === prev[1] );
                } );

                if ( uniqueCoordinates.length < 2 ) {
                    continue;
                }

                // Créer une ligne GeoJSON à partir des coordonnées filtrées
                const line = lineString( uniqueCoordinates );

                try {
                    // Validation des coordonnées avant d'appeler point()
                    if ( typeof position[0] !== "number" || typeof position[1] !== "number" ||
                      isNaN( position[0] ) || isNaN( position[1] ) ) {
                        console.warn( "[LineFollower] Invalid position coordinates for nearestPointOnLine:", position );

                        continue;
                    }

                    // Trouver le point le plus proche sur la ligne
                    const nearestPoint = nearestPointOnLine( line, point( position ) );
                    const distance = nearestPoint.properties.dist * 1000; // convertir en mètres

                    // Si la distance est inférieure au rayon de détection
                    if ( distance <= radius ) {
                        // Créer un feature GeoJSON compatible avec l'interface DetectedLine
                        const feature: GeoJSON.Feature = {
                            "type":       "Feature",
                            "id":         polyline.id, // identifiant stable pour la ligne détectée
                            "geometry":   line.geometry,
                            "properties": {
                                "routeId":   this.drawnItineraryGeometry.id,
                                "segmentId": polyline.id,
                                "legId":     polyline.legId,
                                "type":      polyline.type,
                            },
                        };

                        results.push( {
                            feature,
                            distance,
                        } );
                    }
                } catch( err ) {
                    console.error( "Erreur lors du calcul du point le plus proche:", err );
                }
            }
        } catch( error ) {
            console.error( "Erreur lors de la détection des lignes:", error );
        }

        // Mettre à jour le cache
        const now = Date.now();
        results.forEach( ( { feature } ) => {
            const id = this.getFeatureId( feature );
            this.detectedLines.set( id, {
                feature,
                "lastDetectedTime": now,
            } );
        } );

        // Nettoyer le cache des lignes trop anciennes (> 30s)
        for ( const [ id, data ] of this.detectedLines.entries() ) {
            if ( now - data.lastDetectedTime > 30000 ) {
                this.detectedLines.delete( id );
            }
        }

        return results;
    }

    /**
     * Vérifie si l'utilisateur approche de la fin de la ligne courante
     * Permet de rechercher une nouvelle ligne à suivre avant de perdre la ligne actuelle
     */
    private isNearEndOfLine(): boolean {
        if ( this.currentLine === null || this.currentLine.geometry.coordinates.length === 0 ) {
            return false;
        }

        const coords = this.currentLine.geometry.coordinates;
        const lastIndex = coords.length - 1;

        // Si on est sur un des 3 derniers segments de la ligne
        if ( this.currentLine.lastMatchIndex >= lastIndex - 3 ) {
            // Calculer la distance jusqu'à la fin
            const currentPoint = point( this.state.matchedPosition );
            const endPoint = point( coords[lastIndex] );

            // Distance en mètres (conversion approximative degrés -> mètres)
            const distanceToEnd = 111000 * Math.sqrt( Math.pow( endPoint.geometry.coordinates[0] - currentPoint.geometry.coordinates[0], 2 ) +
              Math.pow( endPoint.geometry.coordinates[1] - currentPoint.geometry.coordinates[1], 2 ) );

            // Si on est à moins de 30 mètres de la fin
            return distanceToEnd < 30;
        }

        return false;
    }

    // Puis modifier la méthode findBestLineToFollow comme suit:

    private findBestLineToFollow(
        position: [number, number],
        nearbyLines: DetectedLine[],
        heading: number | null,
    ): LineMatch | null {
        if ( nearbyLines.length === 0 ) {
            return null;
        }

        /*
         * Stratégie 1: Continuer à suivre la ligne courante si possible
         * ET si on n'est pas proche de la fin de la ligne
         */
        if ( this.currentLine !== null ) {
            const currentId = this.currentLine.id;
            const currentMatch = nearbyLines.find( ( { feature } ) => this.getFeatureId( feature ) === currentId );

            if ( currentMatch !== undefined && currentMatch.distance <= this.maxOffLineDistance ) {
                // Vérifier si on approche de la fin de la ligne
                const nearEnd = this.isNearEndOfLine();

                // Si on n'est pas proche de la fin, continuer avec cette ligne
                if ( !nearEnd ) {
                    if ( currentMatch.feature.geometry.type !== "LineString" ) {
                        return null;
                    }

                    const lineGeometry = currentMatch.feature.geometry;

                    // Validation des coordonnées
                    if ( typeof position[0] !== "number" || typeof position[1] !== "number" ||
                      isNaN( position[0] ) || isNaN( position[1] ) ) {
                        console.warn( "[LineFollower] Invalid position coordinates for current match:", position );

                        return null;
                    }

                    const matchResult = nearestPointOnLine( lineGeometry, point( position ) );

                    // Calculer la confiance en fonction de la distance
                    const confidence = Math.max( 0.1, Math.min(
                        0.95,
                        1 - ( currentMatch.distance / this.maxOffLineDistance ),
                    ) );

                    return {
                        "feature": currentMatch.feature,
                        matchResult,
                        confidence,
                    };
                }

                /*
                 * Si on est proche de la fin, on va chercher immédiatement une autre ligne
                 * en passant à la stratégie 3 (ignorer la stratégie 2 basée sur l'historique)
                 */
            }
        }

        /*
         * Si on arrive ici, c'est soit qu'on n'a pas de ligne courante,
         * soit qu'on est trop loin de la ligne courante,
         * soit qu'on approche de la fin de la ligne courante
         */

        /*
         * On saute directement à la stratégie 3: Trouver la ligne la plus proche
         * Mais on relaxe les conditions pour faciliter la transition
         */
        const sortedLines = [ ...nearbyLines ].sort( ( a, b ) => a.distance - b.distance );
        if ( sortedLines.length === 0 ) {
            return null;
        }

        const closestLine = sortedLines[0];

        // On élargit le seuil si on venait d'une ligne (pour faciliter la transition)
        const transitionThreshold = this.currentLine !== null
            ? this.stickToLineThreshold * 1.5
            : this.stickToLineThreshold;

        if ( closestLine.distance <= transitionThreshold ) {
            if ( closestLine.feature.geometry.type !== "LineString" ) {
                return null;
            }

            const lineGeometry = closestLine.feature.geometry;

            // Validation des coordonnées
            if ( typeof position[0] !== "number" || typeof position[1] !== "number" ||
              isNaN( position[0] ) || isNaN( position[1] ) ) {
                console.warn( "[LineFollower] Invalid position coordinates for closest line:", position );

                return null;
            }

            const matchResult = nearestPointOnLine( lineGeometry, point( position ) );

            // Si on a un cap, on vérifie toujours la direction mais avec un angle plus permissif
            if ( heading !== null ) {
                const index = matchResult.properties?.index ?? 0;
                const lineDirection = this.calculateLineDirection(
                    closestLine.feature,
                    index,
                );

                const angleDiff = Math.abs( heading - lineDirection );
                const normalizedDiff = Math.min( angleDiff, 360 - angleDiff );

                // Angle plus permissif pour les transitions (120° au lieu de 90°)
                if ( normalizedDiff > 120 ) {
                    return null;
                }
            }

            // Calculer la confiance
            const confidence = Math.max( 0.1, Math.min(
                0.85,
                1 - ( closestLine.distance / transitionThreshold ),
            ) );

            // On réduit le seuil de confiance minimale pour les transitions
            const minConfidence = this.currentLine !== null
                ? this.minLineConfidence * 0.8
                : this.minLineConfidence;

            // Ne suivre que si la confiance est suffisante (mais avec un seuil réduit en transition)
            if ( confidence >= minConfidence ) {
                return {
                    "feature": closestLine.feature,
                    matchResult,
                    confidence,
                };
            }
        }

        /*
         * Stratégie 2: Rechercher dans l'historique récent
         * On la place ici comme fallback si aucune ligne proche n'est trouvée
         */
        if ( this.positionHistory.length > 0 ) {
            const recentHistory = this.positionHistory.slice( -5 );
            const recentLineIds = new Set( recentHistory.
                filter( ( h ) => h.lineId !== null ).
                map( ( h ) => h.lineId ) );

            // Rechercher une ligne récemment suivie
            for ( const lineId of recentLineIds ) {
                if ( lineId === null ) {
                    continue;
                }

                const recentMatch = nearbyLines.find( ( { feature } ) => this.getFeatureId( feature ) === lineId );

                if ( recentMatch !== undefined && recentMatch.distance <= this.maxOffLineDistance ) {
                    if ( recentMatch.feature.geometry.type !== "LineString" ) {
                        continue;
                    }

                    const lineGeometry = recentMatch.feature.geometry;

                    // Validation des coordonnées
                    if ( typeof position[0] !== "number" || typeof position[1] !== "number" ||
                      isNaN( position[0] ) || isNaN( position[1] ) ) {
                        console.warn( "[LineFollower] Invalid position coordinates for recent match:", position );

                        continue;
                    }

                    const matchResult = nearestPointOnLine( lineGeometry, point( position ) );

                    // Calculer la confiance
                    const confidence = Math.max( 0.1, Math.min(
                        0.9,
                        1 - ( recentMatch.distance / this.maxOffLineDistance ),
                    ) );

                    return {
                        "feature": recentMatch.feature,
                        matchResult,
                        confidence,
                    };
                }
            }
        }

        return null;
    }

    /**
     * Met à jour la référence à la ligne courante
     */
    private updateCurrentLine( feature: GeoJSON.Feature, matchIndex: number ): void {
        const id = this.getFeatureId( feature );

        if ( feature.geometry.type !== "LineString" ) {
            return;
        }

        const lineStringGeometry = feature.geometry;

        if ( this.currentLine === null || this.currentLine.id !== id ) {
            // Nouvelle ligne
            this.currentLine = {
                id,
                "geometry":       lineStringGeometry,
                "detectedAt":     Date.now(),
                "lastMatchIndex": matchIndex,
            };
        } else {
            // Mettre à jour l'index
            this.currentLine.lastMatchIndex = matchIndex;
        }
    }

    /**
     * Calcule la direction de la ligne à l'index donné
     */
    private calculateLineDirection( feature: GeoJSON.Feature, index: number ): number {
        if ( feature.geometry.type !== "LineString" ) {
            return 0;
        }

        const lineGeometry = feature.geometry;
        const coords = lineGeometry.coordinates;

        // S'assurer que l'index est valide
        const safeIndex = index >= coords.length - 1 ? coords.length - 2 : index;

        // Calculer le cap entre les deux points
        const start = coords[safeIndex];
        const end = coords[safeIndex + 1];

        return bearing( point( start ), point( end ) );
    }

    /**
     * Calcule une bounding box autour d'une position
     */
    private calculateBBox(
        position: [number, number],
        radiusMeters: number,
    ): [[number, number], [number, number]] {
    // Approximation rapide: 1 degré = ~111km à l'équateur
        const radiusDegrees = radiusMeters / 111000;

        return [
            [ position[0] - radiusDegrees, position[1] - radiusDegrees ],
            [ position[0] + radiusDegrees, position[1] + radiusDegrees ],
        ];
    }

    /**
     * Obtient un identifiant unique pour une feature
     */
    private getFeatureId( feature: GeoJSON.Feature ): string {
    // Utiliser l'id de la feature si disponible
        if ( feature.id !== undefined ) {
            return String( feature.id );
        }

        // Sinon, générer un id basé sur la géométrie
        if ( feature.geometry.type === "LineString" ) {
            const lineGeometry = feature.geometry;
            const coords = lineGeometry.coordinates;
            if ( coords.length >= 2 ) {
                const start = coords[0];
                const end = coords[coords.length - 1];

                return `line-${ start[0].toFixed( 5 ) },${ start[1].toFixed( 5 ) }-${ end[0].toFixed( 5 ) },${ end[1].toFixed( 5 ) }`;
            }
        }

        // Fallback: id aléatoire
        return `line-${ Math.random().toString( 36 ).
            substring( 2, 11 ) }`;
    }

    /**
     * Ajoute une position à l'historique
     */
    private addToHistory(
        position: [number, number],
        timestamp: number,
        lineId: string | null,
    ): void {
        this.positionHistory.push( {
            position,
            timestamp,
            lineId,
        } );

        // Limiter la taille de l'historique
        if ( this.positionHistory.length > 20 ) {
            this.positionHistory.shift();
        }
    }

    /**
     * S'abonne aux changements d'état
     */
    public onStateChange( callback: ( state: FollowingState ) => void ): void {
        this.stateChangeCallbacks.push( callback );
    }

    /**
     * Se désabonne des changements d'état
     */
    public offStateChange( callback: ( state: FollowingState ) => void ): void {
        this.stateChangeCallbacks = this.stateChangeCallbacks.filter( ( cb ) => cb !== callback );
    }

    /**
     * Notifie les changements d'état
     */
    private notifyStateChange(): void {
        const state = { ...this.state };
        this.stateChangeCallbacks.forEach( ( callback ) => {
            callback( state );
        } );
    }

    /**
     * Réinitialise le LineFollower
     */
    public reset(): void {
        this.positionHistory = [];
        this.currentLine = null;
        this.detectedLines.clear();

        this.state.onLine = false;
        this.state.lineId = null;
        this.state.confidence = 0;
        this.state.lineDirection = 0;
        this.state.isReversed = false;
        this.state.lastDirectionChangeTime = 0;
    }

    /**
     * Retourne l'état actuel
     */
    public getCurrentState(): FollowingState {
        return { ...this.state };
    }

    /**
     * Obtient la géométrie de la ligne suivie actuellement
     */
    public getCurrentLineGeometry(): GeoJSON.LineString | null {
        if ( this.currentLine === null ) {
            return null;
        }

        return { ...this.currentLine.geometry };
    }

}
